import { ComponentProps } from 'preact';
import { ReactNode } from 'preact/compat';

import { cn } from '@/lib/utils';
import { HabitatListItem } from '@/queries/habitats/types';

import HabitatStatus from '../HabitatCard/HabitatStatus';
import HabitatLink, { HabitatLinkProps } from '../HabitatLink';

interface HabitatGridItemProps extends ComponentProps<'div'> {
  habitat: HabitatListItem;
  renderLink?: (props: HabitatLinkProps) => ReactNode;
}

const HabitatGridItem = ({ habitat, className, renderLink, ...props }: HabitatGridItemProps) => {
  return (
    <div
      className={cn('flex flex-col rounded-2xl bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]', className)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <div className="relative h-[142px] rounded-t-2xl bg-gray-400">
        {habitat.backgroundImage && (
          <img
            src={habitat.backgroundImage}
            alt={habitat.title}
            className="preflight preflight-img size-full overflow-hidden rounded-t-2xl object-cover"
          />
        )}
        <HabitatStatus
          online={habitat.isStreamOn}
          liveTalk={habitat.liveTalk}
          shadow="small"
          className="absolute right-3 top-2"
        />
        <div className="absolute inset-x-0 -bottom-7 flex items-center justify-center">
          {habitat.profileImage ? (
            <img
              src={habitat.profileImage}
              alt={habitat.animal}
              className="preflight preflight-img size-[108px] overflow-hidden rounded-full border border-white object-cover"
            />
          ) : (
            <div className="preflight flex size-[108px] items-center justify-center overflow-hidden rounded-full border border-white bg-gray-400 text-2xl font-bold text-white">
              {habitat.animal?.charAt(0) || 'H'}
            </div>
          )}
        </div>
      </div>
      <div className="grow px-4 pt-8 desktop:pt-9">
        <h3 className="preflight preflight-h3 text-base/tight font-semibold text-black desktop:text-xl/tight">
          {habitat.title}
        </h3>
        <p className="preflight preflight-h4 mt-1 text-xs font-semibold uppercase text-stone-500">
          {habitat.animal} at {habitat.zoo.name}
        </p>
        <p className="preflight preflight-p mt-1 line-clamp-3 text-xs text-stone-600 desktop:mt-2 desktop:text-base">
          {habitat.description}
        </p>
      </div>
      <div className="px-4 pb-6 pt-3">
        {renderLink ? (
          renderLink({
            habitatId: habitat._id,
            slug: habitat.slug,
            zooSlug: habitat.zoo.slug,
            className:
              'preflight preflight-a block w-full rounded-full bg-primary-green px-4 py-2 text-center text-sm font-bold text-white hover:bg-secondary-green desktop:text-base',
            children: 'Visit Habitat',
          })
        ) : (
          <HabitatLink
            habitatId={habitat._id}
            slug={habitat.slug}
            zooSlug={habitat.zoo.slug}
            className="preflight preflight-a block w-full rounded-full bg-primary-green px-4 py-2 text-center text-sm font-bold text-white hover:bg-secondary-green desktop:text-base"
          >
            Visit Habitat
          </HabitatLink>
        )}
      </div>
    </div>
  );
};

export default HabitatGridItem;
